<template>
  <q-dialog v-model="show" persistent>
    <q-card style="min-width: 900px">
      <q-card-section>
        <div class="row items-center">
          <div class="text-h6">AI Reports</div>
          <q-badge color="primary" text-color="white" class="q-ml-sm">Beta</q-badge>
        </div>
        <div style="font-size: 12px">Select a template and generate a new report</div>
      </q-card-section>
      <q-separator v-if="!reportContent.length" />
      <q-card-section v-if="!reportContent.length">
        <div v-if="loading" class="text-center q-pa-md">
          <q-spinner color="primary" size="2em" />
          <div class="text-grey-6 q-mt-sm">Loading templates...</div>
        </div>

        <div v-else-if="error" class="text-center q-pa-md">
          <q-icon name="error" color="negative" size="2em" />
          <div class="text-negative q-mt-sm">{{ error }}</div>
          <q-btn flat color="primary" label="Retry" @click="loadTemplates" class="q-mt-sm" />
        </div>

        <div v-else-if="generating" class="text-center q-pa-md">
          <q-spinner color="primary" size="2em" />
          <div class="text-grey-6 q-mt-sm">Generating report...</div>
          <div class="text-caption text-grey-5 q-mt-xs">
            This may take a few moments (1-3 minutes)
          </div>
        </div>

        <div v-else>
          <q-select
            v-model="selectedTemplate"
            :options="templates"
            option-label="name"
            hide-bottom-space
            option-value="id"
            outlined
            label="Select Template"
            placeholder="Choose a report template"
            :rules="[(val) => !!val || 'Please select a template']"
            class="q-mb-md"
          >
            <template #no-option>
              <q-item>
                <q-item-section class="text-grey"> No templates available </q-item-section>
              </q-item>
            </template>
          </q-select>
        </div>
      </q-card-section>
      <q-editor
        v-if="reportContent.length > 0"
        v-model="reportContent"
        :toolbar="[
          ['left', 'center', 'right', 'justify'],
          ['bold', 'italic', 'underline', 'strike'],
          ['undo', 'redo'],
          ['print', 'fullscreen']
        ]"
        min-height="5rem"
        max-height="500px"
        :disable="reportContent.length === 0 || generating"
      />
      <q-separator v-if="reportContent.length === 0" />
      <q-card-actions align="right">
        <q-btn flat no-caps label="Cancel" @click="cancel" />
        <q-btn
          unelevated
          color="primary"
          no-caps
          label="Generate"
          :loading="generating"
          :disable="!selectedTemplate || generating || reportContent.length > 0 || !hasSimilarities"
          @click="generate"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script lang="ts">
import templater from '@/api/templater'
import type {
  TemplateAIAttributes,
  PropertyDetailsResponse,
  Similarities,
  ExpenseBenchmarksResponse
} from 'common'
import { excelPayload } from 'common'

export default {
  name: 'GenerateReportDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    // Add props for the data needed to generate the report
    similarities: {
      type: Object as () => Record<string, Similarities>,
      default: () => ({})
    },
    subjectDetail: {
      type: Object as () => PropertyDetailsResponse | null,
      default: null
    },
    comparableDetails: {
      type: Array as () => PropertyDetailsResponse[],
      default: () => []
    },
    expenseBenchmarkData: {
      type: Object as () => ExpenseBenchmarksResponse | null,
      default: null
    },
    hasFinancialAnalysis: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      templates: [] as Pick<TemplateAIAttributes, 'id' | 'name'>[],
      selectedTemplate: null as Pick<TemplateAIAttributes, 'id' | 'name'> | null,
      loading: false,
      generating: false,
      error: '',
      reportContent: ''
    }
  },
  computed: {
    show: {
      get() {
        return this.modelValue
      },
      set(value: boolean) {
        this.$emit('update:modelValue', value)
      }
    },
    hasSimilarities() {
      return Object.keys(this.similarities).length > 0
    }
  },
  created() {
    this.loadTemplates()
  },
  methods: {
    async loadTemplates() {
      this.loading = true
      this.error = ''

      try {
        this.templates = await templater.getAllReportTemplates()
      } catch (err) {
        console.error('Failed to load templates:', err)
        this.error = 'Failed to load templates. Please try again.'
      } finally {
        this.loading = false
      }
    },

    async generate() {
      if (!this.selectedTemplate) return

      this.generating = true

      try {
        // Validate that we have the required data
        if (!this.subjectDetail) {
          throw new Error('Subject property data is required to generate a report.')
        }

        if (this.comparableDetails.length === 0) {
          throw new Error('At least one comparable property is required to generate a report.')
        }

        // Create the same payload that's used for Excel export
        const payload = excelPayload(
          this.similarities,
          this.subjectDetail,
          this.comparableDetails,
          this.expenseBenchmarkData
        )

        if (payload === null) {
          throw new Error(
            'Unable to generate payload. Please ensure all required data is available.'
          )
        }

        // Set the same parameters as Excel export
        const useDevFile = false
        const deletedTabs = 'Intro,30-Day Trends,Rent Comps,Unit-Level Data,Specials,Tables'
        const hiddenTabs = !this.hasFinancialAnalysis
          ? 'Financial Analysis,Pro Forma Model'
          : undefined

        // Call the generateReport function with the template ID and payload
        const reportContent = await templater.generateReport(
          this.selectedTemplate.id,
          payload,
          useDevFile,
          hiddenTabs,
          deletedTabs
        )

        // Handle the generated report content (markdown text)
        this.$q.notify({
          type: 'positive',
          message: `Report generated successfully using template: ${this.selectedTemplate.name}`
        })

        // Track the successful report generation
        this.$tracker.generatedReportFromDialog(
          this.selectedTemplate.id.toString(),
          this.selectedTemplate.name
        )

        this.reportContent = reportContent
      } catch (err) {
        console.error('Failed to generate report:', err)
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to generate report. Please try again.'
        this.$q.notify({
          type: 'negative',
          message: errorMessage
        })
      } finally {
        this.generating = false
      }
    },

    cancel() {
      this.show = false
    }
  }
}
</script>
